import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlayIcon } from '@heroicons/react/24/outline';
import { NewPlayPayload, DefaultService, ReactionsResponse } from '../../../generated-api';
import { ReactionButton } from '../../reactions/ReactionButton';

interface NewPlayCardProps {
  payload: NewPlayPayload;
  createdAt: string;
  initialReactions?: ReactionsResponse;
}

export const NewPlayCard: React.FC<NewPlayCardProps> = ({ payload, createdAt, initialReactions }) => {
  const [reactionsData, setReactionsData] = useState<ReactionsResponse | null>(initialReactions || null);

  const fetchReactions = useCallback(async () => {
    try {
      const response = await DefaultService.getPlayReactionsPlaysPlayIdReactionsGet({
        playId: payload.playId,
      });
      setReactionsData(response);
    } catch (err) {
      // Silently fail - reactions are not critical
      console.error('Failed to fetch reactions:', err);
    }
  }, [payload.playId]);

  // Only fetch if initialReactions not provided
  useEffect(() => {
    if (!initialReactions) {
      fetchReactions();
    }
  }, [initialReactions, fetchReactions]);

  const handleReactionChange = useCallback(() => {
    // Refresh reactions when user adds/changes a reaction
    fetchReactions();
  }, [fetchReactions]);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const playerNames = payload.players.map(p => p.playerName).join(', ');
  const winners = payload.winners || [];

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-edge hover:shadow-md transition-shadow duration-200">
      {/* Navigable card content */}
      <Link data-track="feed_new_play_card" to={`/plays/${payload.playId}`} className="block p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 bg-primary-100 rounded-full p-2">
              <PlayIcon className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-heading">New Play Tracked</p>
              <p className="text-xs text-muted">{formatRelativeTime(createdAt)}</p>
            </div>
          </div>
          {payload.gameImage && (
            <img
              src={payload.gameImage}
              alt={payload.gameName}
              className="h-24 max-w-32 rounded object-contain"
            />
          )}
        </div>
        <div className="mt-3">
          <p className="text-sm text-body">
            <span className="font-medium">{playerNames}</span>
            {' just finished a game of '}
            <span className="font-medium text-primary-600">{payload.gameName}</span>
          </p>
          {winners.length > 0 && (
            <p className="text-sm text-body mt-1">
              <span className="font-medium text-yellow-600">🏆 Winner{winners.length > 1 ? 's' : ''}: </span>
              <span className="font-medium">{winners.join(', ')}</span>
            </p>
          )}
        </div>
        {payload.players.length > 0 && (
          <div className="mt-3 flex -space-x-2">
            {payload.players.slice(0, 5).map((player) => (
              player.playerAvatar ? (
                <img
                  key={player.playerId}
                  src={player.playerAvatar}
                  alt={player.playerName}
                  className="h-8 w-8 rounded-full border-2 border-white object-cover"
                  title={player.playerName}
                />
              ) : (
                <div
                  key={player.playerId}
                  className="h-8 w-8 rounded-full border-2 border-white bg-surface-skeleton flex items-center justify-center text-xs font-medium text-body"
                  title={player.playerName}
                >
                  {player.playerName.charAt(0).toUpperCase()}
                </div>
              )
            ))}
            {payload.players.length > 5 && (
              <div className="h-8 w-8 rounded-full border-2 border-white bg-surface-skeleton flex items-center justify-center text-xs font-medium text-body">
                +{payload.players.length - 5}
              </div>
            )}
          </div>
        )}
      </Link>

      {/* ReactionButton outside the Link to prevent navigation interference */}
      <div className="px-4 pb-4 pt-3 border-t border-edge-subtle">
        <ReactionButton
          playId={payload.playId}
          reactionSummary={reactionsData?.summary}
          userReaction={reactionsData?.userReaction}
          onReactionChange={handleReactionChange}
        />
      </div>
    </div>
  );
};
